import React from 'react';
import {
  AlertCircle,
  AlertTriangle,
  Shield,


  FileText,
  CheckCircle,

  Flame
} from 'lucide-react';
import { ExecutiveSummaryJSON, CriticalIssue } from '../../../../types/contract.types';
import DynamicIcon from '../../../common/DynamicIcon';

interface EnhancedExecutiveSummaryProps {
  summary: ExecutiveSummaryJSON;
}

const EnhancedExecutiveSummary: React.FC<EnhancedExecutiveSummaryProps> = ({ summary }) => {
  const getFavorabilityColor = (score: number) => {
    if (score >= 7) return 'text-green-600 bg-green-50';
    if (score >= 5) return 'text-yellow-600 bg-yellow-50';
    if (score >= 3) return 'text-orange-600 bg-orange-50';
    return 'text-red-600 bg-red-50';
  };

  const getFavorabilityText = (score: number) => {
    if (score >= 7) return 'Favorable';
    if (score >= 5) return 'Balanced';
    if (score >= 3) return 'Concerning';
    return 'Unfavorable';
  };

  const getRiskBgColor = (level: string) => {
    switch (level) {
      case 'High':
        return 'bg-red-50 border-red-200 text-red-800';
      case 'Medium':
        return 'bg-orange-50 border-orange-200 text-orange-800';
      case 'Low':
        return 'bg-green-50 border-green-200 text-green-800';
      default:
        return 'bg-gray-50 border-gray-200 text-gray-800';
    }
  };

  const getRiskIcon = (level: string) => {
    switch (level) {
      case 'High':
        return <AlertCircle className="w-5 h-5 text-red-600" />;
      case 'Medium':
        return <AlertTriangle className="w-5 h-5 text-orange-600" />;
      case 'Low':
        return <Shield className="w-5 h-5 text-green-600" />;
      default:
        return <FileText className="w-5 h-5 text-gray-600" />;
    }
  };

  return (
    <div className="space-y-6">
      {/* Overall Score Card */}
      <div className="bg-white rounded-lg shadow-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-2xl font-bold text-gray-800">Executive Summary</h2>
          <div className={`px-6 py-3 rounded-lg ${getFavorabilityColor(summary.overall_favorability_score)}`}>
            <div className="text-center">
              <div className="text-3xl font-bold">{summary.overall_favorability_score}/10</div>
              <div className="text-sm font-medium">{getFavorabilityText(summary.overall_favorability_score)}</div>
            </div>
          </div>
        </div>

        {/* Overview */}
        <div className="bg-gray-50 rounded-lg p-4">
          <h3 className="font-semibold text-gray-700 mb-2 flex items-center">
            <FileText className="w-5 h-5 mr-2 text-blue-600" />
            Contract Overview
          </h3>
          <p className="text-gray-700 leading-relaxed">{summary.overview}</p>
        </div>
      </div>

      {/* Critical Issues */}
      <div className="bg-white rounded-lg shadow-lg p-6">
        <h3 className="text-xl font-bold text-gray-800 mb-4 flex items-center">
          <Flame className="w-6 h-6 mr-2 text-red-600" />
          Critical Issues Requiring Attention
        </h3>
        
        <div className="space-y-4">
          {summary.critical_issues.map((issue: CriticalIssue, index: number) => (
            <div 
              key={index}
              className={`border rounded-lg p-4 ${getRiskBgColor(issue.risk_level)}`}
            >
              <div className="flex items-start space-x-3">
                <div className="flex-shrink-0 mt-1">
                  {getRiskIcon(issue.risk_level)}
                </div>
                <div className="flex-1">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="font-semibold text-lg flex items-center">
                      <DynamicIcon name={issue.icon} className="w-5 h-5 mr-2" />
                      {issue.title}
                    </h4>
                    <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                      issue.risk_level === 'High' ? 'bg-red-600 text-white' :
                      issue.risk_level === 'Medium' ? 'bg-orange-500 text-white' :
                      'bg-green-600 text-white'
                    }`}>
                      {issue.risk_level} Risk
                    </span>
                  </div>
                  
                  <p className="text-gray-700 mb-3">{issue.description}</p>
                  
                  <div className="bg-white bg-opacity-60 rounded p-3">
                    <div className="flex items-start">
                      <CheckCircle className="w-5 h-5 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                      <div>
                        <span className="font-medium text-gray-700">Recommended Action:</span>
                        <p className="text-gray-600 mt-1">{issue.recommended_action}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Quick Stats Summary */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">High Risk Issues</p>
              <p className="text-2xl font-bold text-red-600">
                {summary.critical_issues.filter(i => i.risk_level === 'High').length}
              </p>
            </div>
            <AlertCircle className="w-8 h-8 text-red-200" />
          </div>
        </div>
        
        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Medium Risk Issues</p>
              <p className="text-2xl font-bold text-orange-600">
                {summary.critical_issues.filter(i => i.risk_level === 'Medium').length}
              </p>
            </div>
            <AlertTriangle className="w-8 h-8 text-orange-200" />
          </div>
        </div>
        
        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Total Issues</p>
              <p className="text-2xl font-bold text-gray-700">
                {summary.critical_issues.length}
              </p>
            </div>
            <FileText className="w-8 h-8 text-gray-200" />
          </div>
        </div>
      </div>
    </div>
  );
};

export default EnhancedExecutiveSummary; 